import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatPercentFromHundredthsAxis,
    formatTimeTick,
} from "../../helpers/chart_common";
import {formatTimeRange} from "../../helpers/tooltips";

export class ShaperCircuitRetransmits extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.explainIssues = null;
    }

    title() {
        return "Circuit TCP Retransmits";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Circuit TCP Retransmits</h5><p>Shows the percentage of TCP retransmits over time.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.explainIssues !== null;
    }

    explain() {
        if (!this.explainIssues || this.explainIssues.length === 0) return null;
        return { region: "Circuit TCP Retransmits", issues: this.explainIssues };
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCircuitRetransmitsGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "CircuitDetail" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "circuit_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value);
            this.lastGraphState = value;
            this.explainIssues = buildRetransmitIssues(value);
        }
    }

}

function buildSeriesIssue(label, series, ticks, thresholdRaw) {
    const values = Array.isArray(series) ? series : [];
    const times = Array.isArray(ticks) ? ticks : [];
    const len = Math.min(values.length, times.length);
    if (len < 2) return null;

    let total = 0;
    let above = 0;
    let firstIdx = -1;
    let lastIdx = -1;
    let maxRaw = null;
    for (let i = 0; i < len; i += 1) {
        const v = values[i];
        if (!Number.isFinite(v)) continue;
        total += 1;
        if (maxRaw === null || v > maxRaw) maxRaw = v;
        if (v >= thresholdRaw) {
            above += 1;
            if (firstIdx === -1) firstIdx = i;
            lastIdx = i;
        }
    }
    if (total === 0 || above === 0) return null;
    const timeRange = (firstIdx >= 0 && lastIdx >= 0)
        ? formatTimeRange(times[firstIdx], times[lastIdx])
        : null;
    const pct = Math.round((above / total) * 100);
    const peakPct = maxRaw !== null ? (maxRaw / 100).toFixed(1) : null;
    const thresholdPct = (thresholdRaw / 100).toFixed(0);
    const details = peakPct
        ? `>=${thresholdPct}% for ${pct}% of the time (peak ${peakPct}%)`
        : `>=${thresholdPct}% for ${pct}% of the time`;
    return {
        time_range: timeRange,
        severity: "info",
        issue: `${label} retransmits high`,
        details,
    };
}

function buildRetransmitIssues(data) {
    if (!data) return [];
    const ticks = data.ticks || [];
    const down = data.tcp_retransmits_down_percent || [];
    const up = data.tcp_retransmits_up_percent || [];
    const thresholdRaw = 800; // "red" threshold (values are percent * 100)

    const issues = [];
    const downIssue = buildSeriesIssue("Download", down, ticks, thresholdRaw);
    if (downIssue) issues.push(downIssue);
    const upIssue = buildSeriesIssue("Upload", up, ticks, thresholdRaw);
    if (upIssue) issues.push(upIssue);
    return issues;
}

class ShaperCircuitRetransmitsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("% Retransmits", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        // Add tooltip
        this.option.tooltip = {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: 'rgba(30,30,30,0.92)',
                    color: '#fff',
                    fontFamily: 'inherit',
                    formatter: function(params) {
                        if (params.axisDimension === 'x') {
                            return new Date(params.value * 1000).toLocaleString();
                        }
                        return (Math.abs(params.value) / 100).toFixed(2) + '%';
                    }
                }
            },
            backgroundColor: 'rgba(30,30,30,0.92)',
            textStyle: {
                fontSize: 10,
                color: '#fff',
                fontFamily: 'inherit'
            },
            formatter: function(params) {
                if (!params || params.length === 0) return '';
                let result = '<div style="font-size: 10px;">';
                result += new Date(params[0].axisValue * 1000).toLocaleString() + '<br/>';
                
                params.forEach(function(p) {
                    let value = Math.abs(p.value) / 100;
                    if (p.seriesName === 'Retransmits Down') {
                        result += `Retransmits Down: ${value.toFixed(2)}%<br/>`;
                    } else if (p.seriesName === 'Retransmits Up') {
                        result += `Retransmits Up: ${value.toFixed(2)}%<br/>`;
                    }
                });
                
                result += '</div>';
                return result;
            }
        };
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();

        this.option.legend = { show: false, data: [] };
        applySplitDirectionAxes(this.option, {
            ticks: data.ticks || [],
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatPercentFromHundredthsAxis,
        });
        this.option.series = [
            { name: "Download", type: "line", data: [], smooth: true, showSymbol: false, xAxisIndex: 0, yAxisIndex: 0 },
            { name: "Upload", type: "line", data: [], smooth: true, showSymbol: false, xAxisIndex: 1, yAxisIndex: 0 }
        ];

        for (let x=0; x<data.tcp_retransmits_down_percent.length; x++) {
            this.option.series[0].data.push(data.tcp_retransmits_down_percent[x]);
            this.option.series[1].data.push(0 - data.tcp_retransmits_up_percent[x]);
        }

        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatPercentFromHundredthsAxis,
            formatter: (params) => {
                if (!params || params.length === 0) return "";
                const timeIndex = params[0].dataIndex;
                return `<div style="font-size: 10px;">
${formatTimeTick(params[0].axisValue)}<br/>
<strong>Download retransmits</strong>: ${formatPercentFromHundredthsAxis(data.tcp_retransmits_down_percent[timeIndex], 2)}<br/>
<strong>Upload retransmits</strong>: ${formatPercentFromHundredthsAxis(data.tcp_retransmits_up_percent[timeIndex], 2)}
</div>`;
            },
        });

        finalizeSplitDirectionChart(this.option);
        this.chart.setOption(this.option, true);
    }
}
